import React, { useState, useEffect, useRef } from 'react';
import { Radio, Mic, MicOff, X, Send, Users, Volume2, VolumeX } from 'lucide-react';
import './CommanderPanel.css';

function CommanderPanel({ isActive, onToggle, username, serverId }) {
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [activeCommanders, setActiveCommanders] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Simulate active commanders in the channel
    const commanders = [
      { id: 1, name: username, speaking: false, self: true },
      { id: 2, name: 'Commander Alpha', speaking: true, self: false },
      { id: 3, name: 'Commander Bravo', speaking: false, self: false },
    ];
    setActiveCommanders(commanders);

    // Simulate some commander messages
    setMessages([
      { id: 1, user: 'Commander Alpha', content: 'All commanders, report status.', time: '14:32', self: false },
      { id: 2, user: 'Commander Bravo', content: 'Bravo team ready.', time: '14:33', self: false },
    ]);
  }, [username, serverId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      user: username,
      content: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      self: true
    };
    
    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isActive) {
    return (
      <button className="commander-toggle" onClick={onToggle} title="Open Commander Channel">
        <Radio size={18} />
        <span>Commander Channel</span>
      </button>
    );
  }

  return (
    <div className="commander-panel">
      <div className="commander-header">
        <div className="commander-title">
          <Radio size={18} className="commander-icon" />
          <span>Commander Channel</span>
          <span className="commander-badge">SECURE</span>
        </div>
        <div className="commander-actions">
          <span className="active-count">
            <Users size={14} />
            {activeCommanders.length} Active
          </span>
          <button className="close-btn" onClick={onToggle}>
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="commander-content">
        <div className="commander-voice">
          <div className="voice-header">
            <Volume2 size={14} />
            <span>Voice Channel</span>
          </div>
          <div className="commander-users">
            {activeCommanders.map((commander) => (
              <div 
                key={commander.id} 
                className={`commander-user ${commander.speaking ? 'speaking' : ''} ${commander.self ? 'self' : ''}`}
              >
                <div className="user-avatar">
                  {commander.name[0]}
                  {commander.speaking && <div className="speaking-indicator" />}
                </div>
                <span className="user-name">{commander.self ? `${commander.name} (You)` : commander.name}</span>
                {commander.self && isMuted && <MicOff size={12} className="muted-icon" />}
              </div>
            ))}
          </div>
          <div className="voice-controls">
            <button 
              className={`voice-btn ${isMuted ? 'active' : ''}`}
              onClick={() => setIsMuted(!isMuted)}
            >
              {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <button 
              className={`voice-btn ${isDeafened ? 'active' : ''}`}
              onClick={() => {
                setIsDeafened(!isDeafened);
                if (!isDeafened) setIsMuted(true);
              }}
            >
              {isDeafened ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
        </div>

        <div className="commander-chat">
          <div className="chat-header">
            <span>Text Channel</span>
          </div>
          <div className="chat-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-message ${msg.self ? 'self' : ''}`}>
                <div className="message-header">
                  <span className="message-user">{msg.user}</span>
                  <span className="message-time">{msg.time}</span>
                </div>
                <div className="message-content">{msg.content}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input">
            <input 
              type="text"
              placeholder="Message commanders..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="send-btn" onClick={handleSend}>
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommanderPanel;
